import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { getImageSrc } from '../screens/NewProductUI/ProductDetails';

/**
 * IMAGE PREVIEW SLIDER (LIGHTBOX)
 *
 * Purpose: Display images in a full-screen lightbox with navigation and footer controls
 * for Silo & Lifestyle images on Product Page V1.
 *
 * Key Features Added for Post Edit:
 * 1. editedImages prop - Contains cached blob URLs of edited previews
 *    - Key: image ID
 *    - Value: blob URL from canvas processing
 * 2. editImageLoading prop - Boolean to show/hide loader during preview generation
 *    - true = Show spinner and "Generating..." message
 *    - false = Hide loader, show the image
 *
 * How getImageSrc() works with editedImages:
 * - getImageSrc(image, pass, editedImages)
 * - If pass === POST_EDIT:
 *   - Returns editedImages[imageId] if cached
 *   - Falls back to original if not cached yet
 * - If pass === ORIGINAL:
 *   - Returns render_image_details.file (original)
 *
 * Why loader during generation:
 * - Canvas processing takes 1-2 seconds
 * - Users need visual feedback that system is working
 * - Prevents confusion when image doesn't appear immediately
 */
export default function ImagePreviewSlider({
  title = '',
  imagesList,
  photoIndex,
  setIsOpen,
  setPhotoIndex,
  footer = '',
  handleToggleScreen = () => {},
  pass,
  setPass,
  editedImages = {}, // Cache of blob URLs for Post Edit previews
  editImageLoading = false, // Show loader during preview generation
  infiniteLoop = true, // Show first image after last image or last image before first image
}) {
  if (!imagesList || !imagesList.length) return null;
  return (
    <>
      {/**
       * DISPLAY CORRECT IMAGE BASED ON PASS SELECTION
       *
       * How it works:
       * - pass.current === POST_EDIT + editedImages[id] = Show edited preview (blob URL)
       * - pass.current === ORIGINAL = Show original image
       * - pass.current === STANDARD/FILMIC/COLOR = Show respective render pass
       *
       * The editedImages object contains cached blob URLs generated by handleDownloadEditedImage()
       * when canvas processes the effects. This way we don't regenerate on next view.
       */}
      <Lightbox
        wrapperClassName="productDetails_lightbox_wrapper"
        /*
          react-modal hides the app from screen readers while the lightbox is up.
          Told nothing, it picks document.body - and the browser refuses to honour
          aria-hidden there, since it would hide the lightbox along with the page.
          Pointed at the app root, the page is hidden and the portal is not.
        */
        reactModalProps={{ appElement: document.getElementById('root') }}
        mainSrc={getImageSrc(imagesList[photoIndex], pass, editedImages)}
        nextSrc={
          !infiniteLoop && photoIndex === imagesList.length - 1
            ? null
            : getImageSrc(imagesList[(photoIndex + 1) % imagesList.length], pass, editedImages)
        }
        prevSrc={
          !infiniteLoop && photoIndex === 0
            ? null
            : getImageSrc(
                imagesList[(photoIndex + imagesList.length - 1) % imagesList.length],
                pass,
                editedImages
              )
        }
        onCloseRequest={() => {
          if (window?.document?.fullscreen || window.fullscreen) {
            handleToggleScreen?.();
          }
          setIsOpen(false);

          let _pass = {};
          Object.keys(pass)?.forEach(
            (_item) => (_pass[_item] = { ...pass[_item], current: pass[_item].default })
          );
          setPass(_pass);
        }}
        onMovePrevRequest={() => {
          setPhotoIndex((photoIndex + imagesList.length - 1) % imagesList.length);
        }}
        onMoveNextRequest={() => {
          setPhotoIndex((photoIndex + 1) % imagesList.length);
        }}
        imageCaption={footer || ''}
        imageTitle={title || ''}
      />
      {/**
       * LOADER OVERLAY - SHOWN WHILE GENERATING POST EDIT PREVIEW
       *
       * Why show a loader:
       * - Canvas processing of effects takes 1-2 seconds
       * - Without loader, user thinks nothing is happening
       * - Provides clear feedback: "system is working"
       *
       * Appears when:
       * - editImageLoading === true (during handleDownloadEditedImage processing)
       *
       * Disappears when:
       * - editImageLoading === false (preview generation complete)
       *
       * Visual design:
       * - Full-screen overlay (position: fixed)
       * - Dark semi-transparent background (prevents interaction)
       * - CSS spinner animation (smooth rotating circle)
       * - Text message for clarity
       * - Very high z-index (99999) to appear above lightbox
       */}
      {editImageLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 99999,
            backdropFilter: 'blur(2px)',
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}>
            {/* Animated spinner - rotates 360deg every 1 second */}
            <div
              style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p
              style={{
                color: '#fff',
                fontSize: '16px',
                margin: 0,
                fontWeight: '500',
              }}>
              Generating Post Edit preview...
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
}
